import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FaCheck, FaMagnifyingGlass, FaUser } from "react-icons/fa6";
import { MdClose } from "react-icons/md";
import { driverRoadTestQueryOptions } from "src/api/mvte/driver-road-test-query-options";
import { ExamType } from "../types/exam-type";
import { TenstreetApplicant } from "src/types/tenstreet-applicant";
import { SelectedDriver } from "../types/selected-driver";
import { getFullName } from "../utils/get-full-name";

type SearchFormData = {
    licenseNumber: string;
};

type DriverSearchProps = {
    onSelectDriver: (driver: TenstreetApplicant, examType: ExamType) => void;
    selectedDrivers: SelectedDriver[];
};

function DriverSearch({ onSelectDriver, selectedDrivers }: DriverSearchProps) {
    const [searchLicenseNumber, setSearchLicenseNumber] = useState<{
        licenseNumber: string;
        uniqueId: string;
    }>({
        licenseNumber: "",
        uniqueId: "initial",
    });

    const tenstreetApplicantQuery = useQuery({
        ...driverRoadTestQueryOptions.tenstreetApplicantQueryOptions({
            params: { licenseNumber: searchLicenseNumber.licenseNumber },
            uniqueId: searchLicenseNumber.uniqueId,
        }),
        enabled: searchLicenseNumber.licenseNumber !== "",
    });

    const searchForm = useForm<SearchFormData>();

    async function onSearchSubmit(formData: SearchFormData) {
        const uniqueId = new Date().toISOString();

        setSearchLicenseNumber({
            licenseNumber: formData.licenseNumber,
            uniqueId,
        });
    }

    // Function to clear search after adding driver
    const clearSearch = () => {
        searchForm.reset();
        setSearchLicenseNumber({
            licenseNumber: "",
            uniqueId: "initial",
        });
    };

    // Assign exam and clear search
    const handleSelectDriver = (
        driver: TenstreetApplicant,
        examType: ExamType,
    ) => {
        onSelectDriver(driver, examType);
        clearSearch();
    };

    // Check if the driver from the search is already in the exam session (aka is selected)
    const isDriverInExamSession = selectedDrivers.some(
        ({ driver }) =>
            driver.licenseNumber ===
            tenstreetApplicantQuery.data?.licenseNumber,
    );

    const fullName = getFullName(
        tenstreetApplicantQuery.data?.firstName,
        tenstreetApplicantQuery.data?.middleName,
        tenstreetApplicantQuery.data?.lastName,
    );

    return (
        <div className="p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-64px)]">
            <div className="bg-gray-50 rounded-md shadow-sm border border-gray-200">
                <form
                    className="p-2"
                    onSubmit={searchForm.handleSubmit(onSearchSubmit)}
                >
                    <fieldset className="fieldset mb-0">
                        <legend className="fieldset-legend text-xs font-medium text-gray-600">
                            Driver&apos;s License
                        </legend>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Enter license number"
                                className="input input-bordered input-sm w-full pl-3 pr-8"
                                {...searchForm.register("licenseNumber", {
                                    required: true,
                                })}
                            />
                            <div className="absolute right-1 top-1/2 -translate-y-1/2">
                                <button
                                    className="btn btn-xs btn-circle btn-primary text-white"
                                    type="submit"
                                >
                                    {tenstreetApplicantQuery.isLoading ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <FaMagnifyingGlass className="w-3 h-3" />
                                    )}
                                </button>
                            </div>
                        </div>
                        {searchForm.formState.errors.licenseNumber && (
                            <p className="text-red-500 text-xs mt-1">
                                Required
                            </p>
                        )}
                    </fieldset>
                </form>
            </div>

            {/* Search Results Container - Always visible once search has been initiated */}
            {(tenstreetApplicantQuery.isLoading ||
                tenstreetApplicantQuery.data !== undefined) && (
                <div className="bg-white rounded-md shadow-sm border border-gray-200">
                    <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 rounded-t-md flex justify-between items-center">
                        <h4 className="text-xs font-medium text-gray-700">
                            Search Results
                        </h4>
                        {tenstreetApplicantQuery.data && (
                            <button
                                onClick={clearSearch}
                                className="btn btn-ghost btn-xs"
                            >
                                <MdClose className="w-3 h-3 mr-1" /> Clear
                            </button>
                        )}
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        {/* Loading State */}
                        {tenstreetApplicantQuery.isLoading && (
                            <div className="flex justify-center items-center py-4">
                                <div className="loading loading-spinner loading-sm"></div>
                                <span className="ml-2 text-xs text-gray-600">
                                    Searching...
                                </span>
                            </div>
                        )}

                        {/* Results */}
                        {tenstreetApplicantQuery.data !== undefined &&
                            !tenstreetApplicantQuery.isLoading && (
                                <>
                                    {tenstreetApplicantQuery.data !== null ? (
                                        <div className="p-2">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="bg-gray-100 rounded-full p-1">
                                                    <FaUser className="w-3 h-3 text-gray-600" />
                                                </div>
                                                <p className="text-sm font-medium text-gray-800 truncate">
                                                    {fullName ||
                                                        "Unknown Driver"}
                                                </p>
                                            </div>
                                            <div className="flex space-x-2 pl-6">
                                                <p className="text-xs text-gray-600">
                                                    <span className="font-medium">
                                                        Code:{" "}
                                                    </span>
                                                    <span className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                                                        {tenstreetApplicantQuery
                                                            .data.driverCode ||
                                                            "N/A"}
                                                    </span>
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                    <span className="font-medium">
                                                        License:{" "}
                                                    </span>
                                                    <span className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                                                        {tenstreetApplicantQuery
                                                            .data
                                                            .licenseNumber ||
                                                            "N/A"}
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="mt-2 flex justify-end">
                                                {isDriverInExamSession ? (
                                                    <button
                                                        className="btn btn-xs btn-outline btn-success gap-1"
                                                        disabled
                                                    >
                                                        <FaCheck className="w-3 h-3" />{" "}
                                                        Selected
                                                    </button>
                                                ) : (
                                                    <AssignExamTypeToDriverModal
                                                        driver={
                                                            tenstreetApplicantQuery.data
                                                        }
                                                        driverName={fullName}
                                                        onAssign={
                                                            handleSelectDriver
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-3 text-center">
                                            <div className="text-yellow-500 mb-1">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-5 w-5 mx-auto"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                                    />
                                                </svg>
                                            </div>
                                            <p className="text-xs font-medium text-gray-600">
                                                No driver found
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Verify license number
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                    </div>
                </div>
            )}
        </div>
    );
}

type AssignExamTypeToDriverModalProps = {
    driver: TenstreetApplicant;
    driverName: string;
    onAssign: (driver: TenstreetApplicant, examType: ExamType) => void;
};

type ExamTypeFormData = {
    examTypeID: string;
};

function AssignExamTypeToDriverModal({
    driver,
    driverName,
    onAssign,
}: AssignExamTypeToDriverModalProps) {
    // TODO: Replace with actual exam types from API
    const examTypesQuery = useQuery({
        ...driverRoadTestQueryOptions.driverRoadTestExamTypesQueryOptions(),
    });

    const examTypes = examTypesQuery.data ?? [];

    const dialogRef = useRef<HTMLDialogElement>(null);

    const examForm = useForm<ExamTypeFormData>();

    function handleOpenModal() {
        examForm.reset();
        dialogRef.current?.showModal();
    }

    function handleCloseModal() {
        dialogRef.current?.close();
        examForm.reset();
    }

    function handleAssignExam(formData: ExamTypeFormData) {
        const examTypeID = +formData.examTypeID;

        const selectedExamType = examTypes.find(
            (type) => type.examTypeID === examTypeID,
        );

        if (selectedExamType === undefined) {
            toast.error("Invalid exam type");
            return;
        }

        onAssign(driver, selectedExamType);
        handleCloseModal();
    }

    return (
        <>
            <button
                className="btn btn-xs btn-outline"
                onClick={handleOpenModal}
            >
                Select
            </button>

            <dialog ref={dialogRef} className="modal">
                <div className="modal-box max-w-md p-0 flex flex-col overflow-hidden">
                    <div className="sticky top-0 bg-base-100 z-10 border-b">
                        <div className="px-4 py-3 flex justify-between items-center">
                            <h3 className="font-bold text-base flex items-center">
                                Assign Exam
                                <div className="badge badge-ghost ml-2 text-xs">
                                    {driverName}
                                </div>
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="btn btn-sm btn-circle btn-ghost"
                            >
                                <MdClose className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={examForm.handleSubmit(handleAssignExam)}>
                        <div className="p-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Select Exam Type
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    {...examForm.register("examTypeID", {
                                        required: true,
                                    })}
                                >
                                    <option value="" disabled>
                                        Select an exam type
                                    </option>
                                    {examTypes.map((examType) => (
                                        <option
                                            key={examType.examTypeID}
                                            value={examType.examTypeID}
                                        >
                                            {examType.examTypeName}
                                        </option>
                                    ))}
                                </select>
                                {examForm.formState.errors.examTypeID && (
                                    <label className="label">
                                        <span className="label-text-alt text-error">
                                            Please select an exam type
                                        </span>
                                    </label>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-base-100 border-t px-4 py-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="btn btn-ghost btn-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary btn-sm"
                            >
                                Assign Test
                            </button>
                        </div>
                    </form>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </>
    );
}

export { DriverSearch };
